import { useState, useEffect } from 'react';
import { useAuth } from '../../context/authContext';
import { useNavigate } from 'react-router-dom';

// Icons (using inline SVG instead of Material UI for simplicity)
const VisibilityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const VisibilityOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const LoginForm = ({ onForgotPassword }) => {
  // States
  const [rut, setRut] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const [validRut, setValidRut] = useState(false);
  
  // Custom hooks
  const { loadUser } = useAuth();
  const navigate = useNavigate();
  
  // Environment variables
  const backendURL = import.meta.env.VITE_API_URL;

  // Check form validity
  const isFormValid = validRut && password.length >= 1;

  // Format and validate RUT
  const formatRut = (value) => {
    // Remove everything except digits and 'K/k'
    let clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
    
    // Limit to 9 characters max (8 digits + 1 verification digit)
    clean = clean.slice(0, 9);
    
    // If less than 2 characters, don't format yet
    if (clean.length < 2) return clean;
    
    // Split body and verification digit
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    
    return `${body}-${dv}`;
  };

  // Validate RUT using Chilean algorithm
  const validateRut = (rut) => {
    if (!rut) return false;
    
    // Remove formatting
    const cleanRut = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (cleanRut.length < 2) return false;
    
    const body = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1);
    
    // Check minimum length for body
    if (body.length < 7) return false;
    
    // Calculate verification digit
    let sum = 0;
    let multiplier = 2;
    
    // Iterate from right to left
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i)) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const expectedDV = 11 - (sum % 11);
    let calculatedDV;
    
    if (expectedDV === 11) calculatedDV = '0';
    else if (expectedDV === 10) calculatedDV = 'K';
    else calculatedDV = expectedDV.toString();
    
    return calculatedDV === dv;
  };
  
  // Update RUT validation when rut changes
  useEffect(() => {
    if (formTouched) {
      setValidRut(validateRut(rut));
    }
  }, [rut, formTouched]);

  // Handle RUT input changes
  const handleRutChange = (e) => {
    const input = e.target.value;
    const formatted = formatRut(input);
    setRut(formatted);
    
    if (!formTouched) {
      setFormTouched(true);
    }
  };
  
  // Handle password input changes
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    
    if (!formTouched) {
      setFormTouched(true);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Clear previous messages
    setError('');
    setSuccess('');
    
    // Form validation
    if (!validRut) {
      setError('Por favor, ingrese un RUT válido');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${backendURL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rut, password })
      });

      const data = await response.json();
      
      if (response.ok && data.message === 'Inicio de sesión exitoso') {
        // Clear localStorage items first
        localStorage.removeItem('accessToken');
        localStorage.removeItem('business_name');
        localStorage.removeItem('rut');
        localStorage.removeItem('trade_name');
        
        // Limpiar sessionStorage para que se muestre el pop-up en el nuevo inicio de sesión
        sessionStorage.removeItem('admin_popup_shown');
        
        // Set new localStorage items
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('business_name', data.business_name);
        localStorage.setItem('rut', data.company.rut);
        localStorage.setItem('trade_name', data.company.trade_name);
        
        // Show success message briefly
        setSuccess('Inicio de sesión exitoso. Redirigiendo...');
        
        // Load user data and navigate
        loadUser();
        setTimeout(() => {
          window.location.href = '/home';
        }, 1000);
      } else {
        setError('Credenciales inválidas. Intente nuevamente.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Error de conexión. Por favor, intente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="w-full max-w-md mx-auto" onSubmit={handleSubmit}>
      {/* RUT Field */}
      <div className="mb-4">
        <label
          className="block text-customBlue font-bold mb-1"
          htmlFor="rut"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          RUT Socio <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            id="rut"
            placeholder="Ej: 12345678-9"
            value={rut}
            onChange={handleRutChange}
            className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 
              ${formTouched && !validRut && rut ? 
                'border-red-500 focus:ring-red-300' : 
                'border-customBlue focus:ring-customBlueLight'}
              transition-all duration-200`}
            required
          />
          {formTouched && !validRut && rut && (
            <p className="text-red-500 text-xs mt-1">RUT inválido. Revise el formato e intente nuevamente.</p>
          )}
        </div>
      </div>

      {/* Password Field */}
      <div className="mb-6">
        <label
          className="block text-customBlue font-bold mb-1"
          htmlFor="password"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Contraseña <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            placeholder="Ingrese su contraseña"
            value={password}
            onChange={handlePasswordChange}
            className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 
              ${formTouched && password.length < 1 && password ? 
                'border-red-500 focus:ring-red-300' : 
                'border-customBlue focus:ring-customBlueLight'}
              transition-all duration-200`}
            required
            minLength={1}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-2 top-2 text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </button>
          {formTouched && password.length < 1 && password && (
            <p className="text-red-500 text-xs mt-1">La contraseña debe tener al menos 1 carácter.</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className={`w-full text-white py-2 rounded-md flex justify-center items-center transition-all duration-200 focus:outline-none
          ${isFormValid ? 
            'bg-customRed hover:bg-customRedLight active:bg-customRed' : 
            'bg-gray-400 cursor-not-allowed'}
        `}
        style={{ fontFamily: 'Nunito, sans-serif' }}
        disabled={loading || !isFormValid}
      >
        {loading ? (
          <SpinnerIcon />
        ) : (
          'Iniciar sesión'
        )}
      </button>

      {/* Forgot Password Link */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onForgotPassword}
          className="hover:underline text-customRed focus:outline-none focus:ring-2 focus:ring-customRedLight rounded px-2 py-1"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Olvidé mi contraseña
        </button>
      </div>

      {/* Error or Success Messages */}
      {error && (
        <div className="mt-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <p className="text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {error}
          </p>
        </div>
      )}
      
      {success && (
        <div className="mt-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded" role="alert">
          <p className="text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {success}
          </p>
        </div>
      )}
    </form>
  );
};

export default LoginForm;